import { useMemo } from 'react';
import { SxProps, Theme } from '@mui/material';
import TransactionField from 'app/pages/mobile/pages/mobile-transactions/TransactionField';
import MobileCodingField from 'app/pages/mobile/pages/mobile-transactions/MobileCodingField';
import type { CodingColumnId } from 'app/util/columnVisibility';
import { sentryMask } from 'sentryReplayPrivacy';
import {
  type CodingOptionKey,
  type CodingDisplayValues,
  type WatchedCodingValues,
  type CurrentCodingValues,
  type CodingFieldConfig,
  CODING_FIELD_CONFIGS,
  WATCHED_VALUE_KEYS,
  CURRENT_VALUE_KEYS,
} from 'app/pages/mobile/types/mobileCodingTypes';

interface MobileCodingFieldListProps {
  /** Display mode: 'readonly' for viewing, 'editable' for editing */
  mode: 'readonly' | 'editable';
  /** Visibility checker function from useCodingSystemConfig */
  isVisible: (columnId: CodingColumnId) => boolean;
  /** Get the display label for a column (custom label or default) */
  getLabel: (columnId: CodingColumnId) => string;
  /** Visible column IDs in admin-configured order */
  orderedColumnIds?: CodingColumnId[];
  /** Style props for TransactionField components */
  fieldSx?: SxProps<Theme>;
  /** Whether to invert colors (for dark backgrounds) */
  invertColor?: boolean;

  // Readonly mode props
  /** Display values for readonly mode (from getCodingDisplayValue) */
  displayValues?: CodingDisplayValues | null;
  /** Raw watched values for review step (used with getDisplayName) */
  rawWatchedValues?: WatchedCodingValues;
  /** Function to convert raw values to display strings (for review step) */
  getDisplayName?: (value: unknown) => string;
  /** Default value to show when field is empty */
  emptyValue?: string;

  // Editable mode props
  /** Current transaction coding values for editable mode */
  currentValues?: CurrentCodingValues | null;
  /** Watched form values for editable mode */
  watchedValues?: WatchedCodingValues;
  /** Handler for opening option selection modal */
  onOpenOptions?: (optionKey: CodingOptionKey) => void;
  /** Handler for clearing a selected option */
  onClearOption?: (optionKey: CodingOptionKey) => void;
  /** Whether the form is disabled */
  disabled?: boolean;
  /** Option keys that should be individually disabled (e.g. costCode/costType when project is required) */
  disabledOptionKeys?: CodingOptionKey[];
}

/**
 * Renders a list of coding fields based on visibility configuration.
 * Supports readonly (TransactionField) and editable (MobileCodingField) modes.
 */
export function MobileCodingFieldList({
  mode,
  isVisible,
  getLabel,
  orderedColumnIds,
  fieldSx,
  invertColor = false,
  displayValues,
  rawWatchedValues,
  getDisplayName,
  emptyValue = '',
  currentValues,
  watchedValues,
  onOpenOptions,
  onClearOption,
  disabled,
  disabledOptionKeys,
}: MobileCodingFieldListProps): JSX.Element {
  const orderedFields: CodingFieldConfig[] = useMemo(() => {
    if (!orderedColumnIds) return CODING_FIELD_CONFIGS;
    const configMap = new Map(CODING_FIELD_CONFIGS.map((c) => [c.id, c]));
    return orderedColumnIds.map((id) => configMap.get(id)).filter(Boolean) as CodingFieldConfig[];
  }, [orderedColumnIds]);

  return (
    <>
      {orderedFields.map((field) => {
        if (!isVisible(field.id)) {
          return null;
        }
        // SEE-1834: ERP employee values are person names; other coding values stay visible.
        const isEmployee = field.optionKey === 'employee';

        if (mode === 'readonly') {
          let value: string;

          if (rawWatchedValues && getDisplayName) {
            const watchedKey = WATCHED_VALUE_KEYS[field.optionKey];
            const rawValue = watchedKey ? rawWatchedValues[watchedKey] : undefined;
            value = getDisplayName(rawValue) || emptyValue;
          } else {
            value = displayValues?.[field.valueKey] || emptyValue;
          }

          return (
            <TransactionField
              key={field.id}
              label={getLabel(field.id)}
              value={isEmployee ? <span {...sentryMask}>{value}</span> : value}
              sx={fieldSx}
              invertColor={invertColor}
            />
          );
        }

        const watchedKey = WATCHED_VALUE_KEYS[field.optionKey];
        const currentKey = CURRENT_VALUE_KEYS[field.optionKey];
        const watchedValue = watchedKey ? watchedValues?.[watchedKey] : undefined;
        const currentValue = currentKey ? currentValues?.[currentKey] : undefined;
        const optionKey = field.optionKey;

        return (
          <MobileCodingField
            key={field.id}
            title={getLabel(field.id)}
            currentValue={currentValue as any}
            selectedValue={watchedValue as any}
            onAdd={() => onOpenOptions?.(optionKey)}
            onClear={() => onClearOption?.(optionKey)}
            disabled={disabled || disabledOptionKeys?.includes(optionKey)}
            maskValue={isEmployee}
          />
        );
      })}
    </>
  );
}

export default MobileCodingFieldList;
